// Server clock synchronisation.
//
// Coach workout timers are anchored to a server timestamp (globalStartedAt) and
// each client computes elapsed time from its own clock. If two devices' clocks
// differ (skew), their timers drift apart — bad for synchronised workouts across
// gyms. This module measures the offset between the server clock and the local
// clock so we can use `serverNow()` (a shared, server-relative time) instead of
// raw Date.now() for anything that must stay in sync across users.

import { API_BASE } from './apiBase';

let offsetMs = 0;        // best estimate of (serverTime - localTime)
let bestRtt = Infinity;  // round-trip time of the sample behind the current offset
let inFlight: Promise<void> | null = null;

/** Server-corrected "now" in epoch ms. Falls back to Date.now() until synced. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

/** Current estimated offset (serverTime - localTime), in ms. */
export function getClockOffset(): number {
  return offsetMs;
}

async function sampleOnce(): Promise<void> {
  const t0 = Date.now();
  const res = await fetch(`${API_BASE}/api/time`, { cache: 'no-store' });
  const t1 = Date.now();
  if (!res.ok) return;
  const data = await res.json().catch(() => null);
  const serverTime = Number(data?.now);
  if (!Number.isFinite(serverTime)) return;

  const rtt = t1 - t0;
  // Assume symmetric latency: server time at t1 ≈ serverTime + rtt/2.
  const candidateOffset = serverTime + rtt / 2 - t1;

  // Keep the sample with the smallest round-trip (least jitter = most accurate).
  if (rtt < bestRtt) {
    bestRtt = rtt;
    offsetMs = candidateOffset;
  }
}

/**
 * Measure the server/client clock offset using a few samples. Re-runnable;
 * call on app start, when the app returns to the foreground, and before a
 * coach (global-start) workout. Concurrent calls share one run.
 */
export async function syncServerClock(samples = 4): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    bestRtt = Infinity; // re-evaluate fresh on each full sync
    for (let i = 0; i < samples; i++) {
      try {
        await sampleOnce();
      } catch {
        // ignore network hiccups; keep whatever offset we have
      }
    }
  })();
  try {
    await inFlight;
  } finally {
    inFlight = null;
  }
}
